import React, { useEffect, useState } from 'react';
import { Spin } from 'antd';
import { checkMediaAccessible } from '@/utils/file';
import { PictureOutlined } from '@ant-design/icons';

export interface SafeMediaProps extends React.HTMLAttributes<HTMLDivElement> {
  src?: string;
  mediaType?: 'image' | 'video' | 'auto';
  height?: number | string;
  width?: number | string;
  fallbackText?: string;
  isRecentlyUploaded?: boolean; // 是否是最近上传的资源（用于转码中的提示）
  onClick?: (e: React.MouseEvent) => void;
}

const SafeMedia: React.FC<SafeMediaProps> = ({
  src,
  mediaType = 'auto',
  height,
  width,
  fallbackText = '资源不存在',
  isRecentlyUploaded = false,
  onClick,
  style,
  className,
  ...props
}) => {
  const [status, setStatus] = useState<'loading' | 'valid' | 'invalid'>('loading');

  useEffect(() => {
    let active = true;
    setStatus('loading');
    checkMediaAccessible(src || '').then((valid) => {
      if (active) {
        setStatus(valid ? 'valid' : 'invalid');
      }
    });
    return () => {
      active = false;
    };
  }, [src]);

  const isVideo =
    mediaType === 'video' ||
    (mediaType === 'auto' && /\.(mp4|webm|ogg|mov)($|\?)/i.test(src || ''));

  // 容器/占位的基础样式
  const boxStyle: React.CSSProperties = {
    height: height || 'auto',
    width: width || (height ? (Number(height) * 16) / 9 : 'auto'),
    minWidth: 80,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 4,
    border: '1px solid #eee',
    ...style,
  };

  if (status === 'loading') {
    return (
      <div className={className} style={{ ...boxStyle, background: '#f5f5f5' }} {...props}>
        <Spin size="small" />
      </div>
    );
  }

  if (status === 'invalid') {
    // 如果是最近上传的资源(可能视频转码还没完成，导致资源也是不可访问的)，只显示纯文本提示（转码中）
    if (isRecentlyUploaded) {
      return <span style={{ fontSize: 12, color: '#999' }}>{fallbackText}</span>;
    }

    // 正常的资源不存在状态(资源被删除了)，显示完整的容器
    return (
      <div
        className={className}
        style={{
          ...boxStyle,
          background: '#f5f5f5',
          color: '#ccc',
          cursor: 'not-allowed',
          flexDirection: 'column',
        }}
        {...props}
      >
        <PictureOutlined style={{ fontSize: 24, marginBottom: 4 }} />
        <span style={{ fontSize: 10 }}>{fallbackText}</span>
      </div>
    );
  }

  // 有效状态的样式
  const contentStyle: React.CSSProperties = {
    height: height || 'auto',
    width: width || 'auto',
    objectFit: 'contain',
    cursor: onClick ? 'pointer' : 'default',
    borderRadius: 4,
    ...style,
  };

  const handleClick = (e: React.MouseEvent) => {
    if (onClick) onClick(e);
  };

  if (isVideo) {
    return (
      <video
        src={src}
        style={contentStyle}
        className={className}
        onClick={handleClick}
        muted
        playsInline
        preload="metadata"
      />
    );
  }

  return (
    <img
      src={src}
      style={contentStyle}
      className={className}
      onClick={handleClick}
      onError={() => setStatus('invalid')}
      alt="media"
    />
  );
};

export default SafeMedia;
